import { TaskType } from '../../types';

export interface ITaskPayload {
    patientID: number | null;
    healthcareFacilityID: number | null;
    availableDate: Date | null;
    dueDate: Date | null;
    taskTypeID: TaskType;
    questionnaireID: number | null;
    collaboratorID: number | null;
    biomarkerSpecimenID: number | null;
    testKitOrderLineID: number | null;
    
    // NEW: Recurring Task Fields (minimal)
    isRecurring?: boolean;
    recurrenceType?: 'DAILY_AM' | 'DAILY_PM' | 'DAILY_NIGHT' | 'WEEKLY' | 'MONTHLY';
    recurrenceInterval?: number;
    recurrenceEndDate?: Date | null;
    templateName?: string;
    expirationDays?: number;

    // Notification on task creation
    sendNotificationOnCreate?: boolean;
}

export interface ITask extends ITaskPayload {
    id: number;
    patientName?: string;
    patientBirthday?: Date;
    description?: string;
    completedBy?: string;
    dateOrdered: Date | null;
    completionDate: Date | null;
    status?: string;
    statusDetail?: string;
    testKitNumber?: string;
    trackingNumber?: string;
    lastReminderSentDate?: Date | null;
    lastReminderSentBy?: string | null;
}

export class Task implements ITask {
    id: number;
    patientID: number | null;
    healthcareFacilityID: number | null;
    taskTypeID: TaskType;
    availableDate: Date | null;
    dueDate: Date | null;
    questionnaireID: number | null;
    collaboratorID: number | null;
    biomarkerSpecimenID: number | null;
    testKitOrderLineID: number | null;
    patientName?: string;
    patientBirthday?: Date;
    description?: string;
    completedBy?: string;
    dateOrdered: Date | null;
    completionDate: Date | null;
    status?: string;
    statusDetail?: string;
    testKitNumber?: string;
    trackingNumber?: string;
    lastReminderSentDate?: Date | null;
    lastReminderSentBy?: string | null;
    
    // NEW: Recurring Task Fields (minimal)
    isRecurring?: boolean;
    recurrenceType?: 'DAILY_AM' | 'DAILY_PM' | 'DAILY_NIGHT' | 'WEEKLY' | 'MONTHLY';
    recurrenceInterval?: number;
    recurrenceEndDate?: Date | null;
    templateName?: string;
    expirationDays?: number;

    constructor(data: ITask) {
        this.id = data.id;
        this.patientID = data.patientID;
        this.healthcareFacilityID = data.healthcareFacilityID;
        this.taskTypeID = data.taskTypeID;
        this.availableDate = data.availableDate ? new Date(data.availableDate) : null;
        this.dueDate = data.dueDate ? new Date(data.dueDate) : null;
        this.questionnaireID = data.questionnaireID;
        this.collaboratorID = data.collaboratorID;
        this.biomarkerSpecimenID = data.biomarkerSpecimenID;
        this.testKitOrderLineID = data.testKitOrderLineID;
        this.patientName = data.patientName;
        this.patientBirthday = data.patientBirthday;
        this.description = data.description;
        this.completedBy = data.completedBy;
        this.dateOrdered = data.dateOrdered ? new Date(data.dateOrdered) : null;
        this.completionDate = data.completionDate ? new Date(data.completionDate) : null;
        this.status = data.status;
        this.statusDetail = data.statusDetail;
        this.testKitNumber = data.testKitNumber;
        this.trackingNumber = data.trackingNumber;
        this.lastReminderSentDate = data.lastReminderSentDate ? new Date(data.lastReminderSentDate) : null;
        this.lastReminderSentBy = data.lastReminderSentBy;
        
        // NEW: Initialize recurring fields
        this.isRecurring = data.isRecurring;
        this.recurrenceType = data.recurrenceType;
        this.recurrenceInterval = data.recurrenceInterval;
        this.recurrenceEndDate = data.recurrenceEndDate ? new Date(data.recurrenceEndDate) : null;
        this.templateName = data.templateName;
        this.expirationDays = data.expirationDays;
    }
}

export interface INewTaskPayload extends ITaskPayload {
    testKitNumber: string;
}

export interface INewTask extends INewTaskPayload {
    dateOrdered: string;
    completedBy: string;
    healthcareFacilities: { id: number; name: string }[];
    patients: { id: number; name: string }[];
    taskTypes: { id: number; name: string }[];
    questionnaires: { id: number; name: string }[];
    collaborators: { id: number; name: string; role: string }[];
    biomarkerSpecimens: { id: number; name: string }[];
    externalBiomarkerSpecimens: { id: number; name: string }[];
}

export class NewTask implements INewTask {
    patientID: number | null;
    healthcareFacilityID: number | null;
    completedBy: string;
    taskTypeID: TaskType;
    availableDate: Date | null;
    dueDate: Date | null;
    questionnaireID: number | null;
    collaboratorID: number | null;
    biomarkerSpecimenID: number | null;
    testKitOrderLineID: number | null;
    testKitNumber: string;
    dateOrdered: string;
    healthcareFacilities: { id: number; name: string }[];
    patients: { id: number; name: string }[];
    taskTypes: { id: number; name: string }[];
    questionnaires: { id: number; name: string }[];
    collaborators: { id: number; name: string; role: string }[];
    biomarkerSpecimens: { id: number; name: string }[];
    externalBiomarkerSpecimens: { id: number; name: string }[];

    constructor(data: INewTask) {
        this.patientID = data.patientID;
        this.healthcareFacilityID = data.healthcareFacilityID;
        this.completedBy = data.completedBy;
        this.taskTypeID = data.taskTypeID;
        this.availableDate = data.availableDate;
        this.dueDate = data.dueDate;
        this.questionnaireID = data.questionnaireID;
        this.collaboratorID = data.collaboratorID;
        this.biomarkerSpecimenID = data.biomarkerSpecimenID;
        this.testKitOrderLineID = data.testKitOrderLineID;
        this.testKitNumber = data.testKitNumber;
        this.dateOrdered = data.dateOrdered;
        this.healthcareFacilities = data.healthcareFacilities;
        this.patients = data.patients;
        this.taskTypes = data.taskTypes;
        this.questionnaires = data.questionnaires;
        this.collaborators = data.collaborators;
        this.biomarkerSpecimens = data.biomarkerSpecimens;
        this.externalBiomarkerSpecimens = data.externalBiomarkerSpecimens;
    }

    get healthcareFacilitiesCombobox() {
        return this.toCombobox(this.healthcareFacilities);
    }

    get patientsCombobox() {
        return this.toCombobox(this.patients);
    }

    get taskTypesCombobox() {
        return this.toCombobox(this.taskTypes);
    }

    get questionnairesCombobox() {
        return this.toCombobox(this.questionnaires);
    }

    get collaboratorsCombobox() {
        return this.toCombobox(this.collaborators);
    }

    get biomarkersCombobox() {
        return this.toCombobox(this.biomarkerSpecimens);
    }

    get externalBiomarkersCombobox() {
        return this.toCombobox(this.externalBiomarkerSpecimens);
    }

    private toCombobox<T extends { id: number; name: string }>(items: T[] = []): { value: string; label: string }[] {
        return items?.map((item) => ({
            value: item.id.toString(),
            label: item.name,
        }));
    }
}

export interface TestKit {
    healthcareFacilityID: number;
    testKitOrderLineID: number;
    sampleNumber: string;
}

export interface ITaskSummary {
    id: number;
    availableDate: string;
    dueDate: string;
    isLate: boolean;
    description: string;
    taskTypeID: TaskType;
    type: string;
    questionnaireID: number;
    collaboratorID: number;
    trackingNumber?: string;
    // NEW: Recurring task indicator (minimal)
    isRecurring: boolean;
    recurrenceType?: 'DAILY_AM' | 'DAILY_PM' | 'DAILY_NIGHT' | 'WEEKLY' | 'MONTHLY';
    // Calendar days from the care-plan event date (Day 0) to the due date;
    // null when the task was not generated from a plan.
    carePlanDayNumber?: number | null;
    // PostConsent / EventRelativePre / EventRelativePost when generated from a care plan.
    carePlanSchedulingPhase?: string | null;
    // The plan's configured event label (e.g. "Surgery date") in the active
    // language; personalises the scheduling-phase badge. Null when the task is
    // not from a plan or the plan defines no event label.
    eventLabel?: string | null;
}

export class TaskSummary implements ITaskSummary {
    id: number;
    availableDate: string;
    dueDate: string;
    isLate: boolean;
    description: string;
    taskTypeID: TaskType;
    type: string;
    questionnaireID: number;
    collaboratorID: number;
    trackingNumber?: string;
    // NEW: Recurring task indicator (minimal)
    isRecurring: boolean;
    recurrenceType?: 'DAILY_AM' | 'DAILY_PM' | 'DAILY_NIGHT' | 'WEEKLY' | 'MONTHLY';
    carePlanDayNumber?: number | null;
    carePlanSchedulingPhase?: string | null;
    eventLabel?: string | null;

    constructor(input: ITaskSummary) {
        this.id = input.id;
        this.availableDate = input.availableDate;
        this.dueDate = input.dueDate;
        this.isLate = input.isLate;
        this.description = input.description;
        this.taskTypeID = input.taskTypeID;
        this.type = input.type;
        this.questionnaireID = input.questionnaireID;
        this.collaboratorID = input.collaboratorID;
        this.trackingNumber = input.trackingNumber;
        // NEW: Initialize recurring indicator
        this.isRecurring = input.isRecurring;
        this.recurrenceType = input.recurrenceType;
        this.carePlanDayNumber = input.carePlanDayNumber;
        this.carePlanSchedulingPhase = input.carePlanSchedulingPhase;
        this.eventLabel = input.eventLabel;
    }
}
